import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Package, FileCode, Trash2, AlertTriangle,
    TrendingDown, Activity, Info, BarChart3
} from 'lucide-react'

type TabType = 'overview' | 'chunks' | 'deadcode' | 'tips'

interface ChunkInfo {
    name: string
    size: number
    modules: number
    type: 'entry' | 'vendor' | 'lazy' | 'asset'
}

const CHUNKS: ChunkInfo[] = [
    { name: 'index.js', size: 185, modules: 42, type: 'entry' },
    { name: 'vendor-react.js', size: 128, modules: 8, type: 'vendor' },
    { name: 'vendor-supabase.js', size: 67, modules: 12, type: 'vendor' },
    { name: 'vendor-lucide.js', size: 45, modules: 1, type: 'vendor' },
    { name: 'vendor-sonner.js', size: 12, modules: 1, type: 'vendor' },
    { name: 'index.css', size: 14, modules: 1, type: 'asset' },
]

const DEAD_CODE = [
    { file: 'src/vendor_core.js.bak', size: '8.2 KB', reason: 'Backup obsoleto (migrado a .ts)', severity: 'high' as const },
    { file: 'src/data/sugerencias.ts', size: '1.1 KB', reason: 'Solo importado en sugerencias.md (no TSX)', severity: 'low' as const },
    { file: 'Sidebar (componente)', size: '~3 KB', reason: 'Componente definido pero nunca renderizado (reemplazado por AppShell)', severity: 'medium' as const },
]

export default function BundlePage() {
    const [tab, setTab] = useState<TabType>('overview')

    const totalSize = CHUNKS.reduce((acc, c) => acc + c.size, 0)
    const vendorSize = CHUNKS.filter(c => c.type === 'vendor').reduce((acc, c) => acc + c.size, 0)
    const appSize = CHUNKS.filter(c => c.type === 'entry').reduce((acc, c) => acc + c.size, 0)

    const tabs = [
        { id: 'overview' as const, label: 'Overview', icon: <BarChart3 size={16} /> },
        { id: 'chunks' as const, label: 'Chunks', icon: <Package size={16} /> },
        { id: 'deadcode' as const, label: 'Dead Code', icon: <Trash2 size={16} /> },
        { id: 'tips' as const, label: 'Optimización', icon: <TrendingDown size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Package className="text-neon-blue" /> Bundle Analyzer
                </h1>
                <p className="text-gray-500 text-sm">Análisis de tamaño de bundle y detección de dead code</p>
            </div>

            <FeatureNotEnabled moduleName="Bundle Analyzer" />

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                    { label: 'Total Bundle', value: `${totalSize} KB`, color: 'text-blue-400' },
                    { label: 'App Code', value: `${appSize} KB`, color: 'text-neon-green' },
                    { label: 'Vendors', value: `${vendorSize} KB`, color: 'text-purple-400' },
                    { label: 'Dead Code', value: String(DEAD_CODE.length), color: 'text-red-400' },
                ].map((s, i) => (
                    <Card key={i} className="!p-4 text-center">
                        <div className="text-xl font-bold font-mono text-white">{s.value}</div>
                        <div className={`text-[10px] uppercase tracking-wider mt-1 ${s.color}`}>{s.label}</div>
                    </Card>
                ))}
            </div>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'overview' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4">Distribución del Bundle</h2>
                    <div className="space-y-3">
                        {CHUNKS.map(chunk => {
                            const pct = Math.round((chunk.size / totalSize) * 100)
                            return (
                                <div key={chunk.name}>
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <code className="text-neon-blue text-xs font-mono">{chunk.name}</code>
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${chunk.type === 'entry' ? 'bg-blue-500/10 text-blue-400' : chunk.type === 'vendor' ? 'bg-purple-500/10 text-purple-400' : 'bg-gray-500/10 text-gray-400'}`}>{chunk.type}</span>
                                        </div>
                                        <span className="text-gray-400 text-xs font-mono">{chunk.size} KB ({pct}%)</span>
                                    </div>
                                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full transition-all ${chunk.type === 'entry' ? 'bg-blue-500' : chunk.type === 'vendor' ? 'bg-purple-500' : 'bg-gray-500'}`} style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </Card>
            )}

            {tab === 'chunks' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Package size={16} className="text-neon-blue" /> Detalle de Chunks</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-white/5">
                                <th className="text-left text-gray-500 py-2 px-3 text-xs">Chunk</th>
                                <th className="text-center text-gray-500 py-2 px-3 text-xs">Tipo</th>
                                <th className="text-center text-gray-500 py-2 px-3 text-xs">Tamaño</th>
                                <th className="text-center text-gray-500 py-2 px-3 text-xs">Módulos</th>
                            </tr></thead>
                            <tbody>
                                {CHUNKS.map((c, i) => (
                                    <tr key={i} className="border-b border-white/5">
                                        <td className="py-2.5 px-3 font-mono text-xs text-neon-blue">{c.name}</td>
                                        <td className="py-2.5 px-3 text-center text-xs text-gray-400">{c.type}</td>
                                        <td className="py-2.5 px-3 text-center text-xs font-mono text-white">{c.size} KB</td>
                                        <td className="py-2.5 px-3 text-center text-xs text-gray-400">{c.modules}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {tab === 'deadcode' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Trash2 size={16} className="text-red-400" /> Dead Code Detectado</h2>
                    <div className="space-y-2">
                        {DEAD_CODE.map((d, i) => (
                            <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${d.severity === 'high' ? 'bg-red-500/5 border border-red-500/10' : d.severity === 'medium' ? 'bg-yellow-500/5 border border-yellow-500/10' : 'bg-white/[0.02] border border-white/5'}`}>
                                <div className="flex items-center gap-3">
                                    {d.severity === 'high' ? <AlertTriangle size={14} className="text-red-400" /> : d.severity === 'medium' ? <AlertTriangle size={14} className="text-yellow-400" /> : <FileCode size={14} className="text-gray-500" />}
                                    <div>
                                        <code className="text-white text-xs font-mono">{d.file}</code>
                                        <p className="text-gray-500 text-xs mt-0.5">{d.reason}</p>
                                    </div>
                                </div>
                                <span className="text-gray-600 text-xs font-mono">{d.size}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'tips' && (
                <div className="space-y-4">
                    {[
                        { title: 'Code Splitting', icon: '✂️', tips: ['Lazy load de páginas con React.lazy()', 'Dynamic imports para módulos pesados', 'Route-based splitting automático con Vite'] },
                        { title: 'Tree Shaking', icon: '🌲', tips: ['Usar imports nombrados vs default', 'Evitar barrel files (index.ts re-exports)', 'Marcar sideEffects: false en package.json'] },
                        { title: 'Compresión', icon: '📦', tips: ['Habilitar Brotli en servidor (50% menos que gzip)', 'Minificar CSS con cssnano', 'Usar terser para JS production'] },
                    ].map(s => (
                        <Card key={s.title}>
                            <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-2"><span>{s.icon}</span> {s.title}</h3>
                            <ul className="space-y-1.5">
                                {s.tips.map((t, j) => (
                                    <li key={j} className="flex items-start gap-2 text-sm text-gray-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-neon-blue mt-1.5 shrink-0" />{t}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    )
}
